import {
  composeValidators,
  currencyMask,
  currencyUnmask,
  isRequired,
} from '@belong/common';
import {
  ButtonSelectorField,
  Form,
  Spinner,
  Text,
  TextField,
} from '@belong/ui';
import { RESIDENT_PRE_QUALIFICATION_STRINGS as STRINGS } from '../resident-pre-qualification.strings';
import { CreditScoreBand } from '../types';
import { FitCheckExplainer } from './fit-check-explainer';

const MAX_ANNUAL_INCOME = 100000000;

const CREDIT_SCORE_BANDS: CreditScoreBand[] = ['Poor', 'Fair', 'Good'];

export type PreQualificationFormValues = {
  annualHouseholdIncome: string;
  creditScoreBand: CreditScoreBand;
};

type FormValues = {
  annualHouseholdIncome: string;
  creditScoreBand?: CreditScoreBand;
};

const INITIAL_VALUES: FormValues = { annualHouseholdIncome: '' };

function isWithinIncomeRange(value: string) {
  if (Number(value) > MAX_ANNUAL_INCOME) {
    return STRINGS['form.incomeMaxValidation'];
  }
}

type Props = {
  isSubmitting: boolean;
  errorMessage?: string;
  onSubmit: (values: PreQualificationFormValues) => void | Promise<void>;
};

export function PreQualificationForm({
  isSubmitting,
  errorMessage,
  onSubmit,
}: Props) {
  return (
    <div className="w-full">
      {isSubmitting && <Spinner />}
      <div className="mb-2xl">
        <FitCheckExplainer />
      </div>
      <Text variant="h2" fontWeight="semibold" className="text-center">
        {STRINGS['form.title']}
      </Text>
      <Text className="mt-sm text-center">{STRINGS['form.subtitle']}</Text>
      <Form<FormValues>
        initialValues={INITIAL_VALUES}
        onSubmit={async ({ annualHouseholdIncome, creditScoreBand }) => {
          if (creditScoreBand) {
            await onSubmit({ annualHouseholdIncome, creditScoreBand });
          }
        }}
        validateOnMount
      >
        {() => (
          <div>
            <Text variant="h3" fontWeight="semibold" className="mt-2xl">
              {STRINGS['form.incomeQuestion']}
            </Text>
            <div className="mt-sm">
              <TextField
                name="annualHouseholdIncome"
                label={STRINGS['form.incomeLabel']}
                mask={currencyMask}
                unmask={currencyUnmask}
                validate={composeValidators(isRequired, isWithinIncomeRange)}
                errorMessageVisible
              />
            </div>
            <Text variant="h3" fontWeight="semibold" className="mt-2xl">
              {STRINGS['form.creditQuestion']}
            </Text>
            <div className="mt-sm">
              <ButtonSelectorField<CreditScoreBand>
                name="creditScoreBand"
                itemHeight="fluid"
                itemClassName="h-[98px]"
                containerClassName="md:gap-sm"
                validate={isRequired}
                errorMessageVisible
                options={CREDIT_SCORE_BANDS.map((band) => ({
                  value: band,
                  renderContent: (isSelected) => (
                    <div className="gap-2xs flex flex-col px-xs text-center">
                      <Text
                        fontWeight="semibold"
                        className={isSelected ? 'text-white' : 'text-navy'}
                      >
                        {STRINGS[`creditBand.${band}.title`]}
                      </Text>
                      <Text
                        variant="p1"
                        className={isSelected ? 'text-white' : 'text-dark-gray'}
                      >
                        {STRINGS[`creditBand.${band}.hint`]}
                      </Text>
                    </div>
                  ),
                }))}
              />
            </div>
            {errorMessage && (
              <div className="mt-sm">
                <Form.ErrorMessage message={errorMessage} />
              </div>
            )}
            <div className="mt-2xl flex justify-center">
              <Form.SubmitButton
                size="fluid"
                className="whitespace-nowrap md:w-auto md:px-xl"
              >
                {STRINGS['form.submit']}
              </Form.SubmitButton>
            </div>
          </div>
        )}
      </Form>
    </div>
  );
}
